import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Heading, Flex, Text, Button, Spinner } from '@radix-ui/themes';
import {
  useChangelogReleases,
  useCreateRelease,
  useUpdateRelease,
  useDeleteRelease,
} from './useChangelogReleases';
import { isSiteAdmin } from '../../lib/utils/roles';
import ReleaseFormDialog from './ReleaseFormDialog';
import ReleaseCard from './ReleaseCard';
import styles from './Changelog.module.css';

const Changelog = () => {
  const { releases, isLoading, error } = useChangelogReleases();
  const { userType } = useSelector(state => state.currentUser);
  const canEdit = isSiteAdmin(userType);

  const createMutation = useCreateRelease();
  const updateMutation = useUpdateRelease();
  const deleteMutation = useDeleteRelease();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);

  const handleAddEntry = () => {
    setEditingRelease(null);
    setDialogOpen(true);
  };

  const handleEdit = release => {
    setEditingRelease(release);
    setDialogOpen(true);
  };

  const handleDelete = release => {
    if (window.confirm(`Delete "${release.title}"?`)) {
      deleteMutation.mutate(release.id, {
        onSuccess: () => {
          setDialogOpen(false);
          setEditingRelease(null);
        },
      });
    }
  };

  const handleSubmit = data => {
    const mutation = data.id ? updateMutation : createMutation;
    mutation.mutate(data, {
      onSuccess: () => {
        setDialogOpen(false);
        setEditingRelease(null);
      },
    });
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="3" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.emptyState}>
        <Text>Failed to load changelog. Please try again later.</Text>
      </div>
    );
  }

  return (
    <Flex direction="column" className={styles.container}>
      <div className={styles.header}>
        <Flex justify="between" align="center">
          <div>
            <Heading size="6" m="0">
              Changelog
            </Heading>
            <Text color="gray" size="3">
              Stay up to date with the latest features and improvements.
            </Text>
          </div>
          {canEdit && (
            <Button size="2" onClick={handleAddEntry}>
              <i className="fa-solid fa-plus" /> Add Entry
            </Button>
          )}
        </Flex>
      </div>

      {releases.length === 0 ? (
        <div className={styles.emptyState}>
          <Text>No changelog entries yet.</Text>
        </div>
      ) : (
        releases.map(release => (
          <ReleaseCard
            key={release.id}
            release={release}
            canEdit={canEdit}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ))
      )}

      {canEdit && (
        <ReleaseFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          release={editingRelease}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </Flex>
  );
};

export default Changelog;
